import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { FullPageSpinner } from '@/components/FullPageSpinner';
import {
  SECURITY_SETTINGS_PATH,
  signInPathFor,
  TooManySecondFactorsError,
  useAuth,
  useSessionState,
  useSignOut,
} from '@/auth';
import { messages } from '@/i18n';
import { AuthScreenLayout } from './AuthScreenLayout';
import { ErrorAlert } from './mfa/mfaShared';
import { secondFactorErrorMessage } from './mfa/mfaUtils';
import { TotpEnrollmentStep } from './mfa/TotpEnrollmentStep';

/**
 * /security/replace-authenticator — where SessionGuard sends a user who signed
 * in with the SMS recovery factor. SMS sign-in grants no API access; it only
 * permits replacing the authenticator. Also reached voluntarily from
 * /security.
 *
 * The order is fixed: enroll the new TOTP, then remove the old one, then sign
 * out and back in with the new one. Removing a factor ends the session, so
 * removal goes last. If the provider refuses a second TOTP factor, the old one
 * goes first instead and the user finishes after signing in again with SMS —
 * this screen, with nothing left to remove.
 *
 * Inside AuthGuard, outside SessionGuard, for the same reason as
 * /security/enroll.
 */
export function ReplaceAuthenticator() {
  const t = messages.mfa;
  const { secondFactors, removeSecondFactor } = useAuth();
  const { status } = useSessionState();
  const signOut = useSignOut();
  // The authenticators to retire: whatever TOTP factors existed before this
  // screen enrolled a new one.
  const [oldTotpIds] = useState(() =>
    secondFactors.filter((f) => f.kind === 'totp').map((f) => f.id),
  );
  const [phase, setPhase] = useState<'enroll' | 'refused' | 'removing'>('enroll');
  const [error, setError] = useState<string | null>(null);

  if (status === 'not-provisioned') return <Navigate to="/access-not-provisioned" replace />;

  const voluntary = status === 'ready';

  const removeOldAndSignOut = async () => {
    setPhase('removing');
    setError(null);
    try {
      for (const id of oldTotpIds) {
        await removeSecondFactor(id);
      }
    } catch (err) {
      setError(secondFactorErrorMessage(err));
      setPhase('refused');
      return;
    }
    await signOut({ redirectTo: signInPathFor('second-factors-changed') });
  };

  if (phase === 'removing') return <FullPageSpinner label={t.replacingOld} />;

  return (
    <AuthScreenLayout
      title={t.replaceTitle}
      description={voluntary ? t.replaceVoluntaryBody : t.replaceBody}
    >
      {phase === 'refused' ? (
        <div className="flex flex-col gap-4">
          <ErrorAlert message={error} />
          <p className="text-sm">{t.replaceRemoveOldFirstBody}</p>
          <Button className="w-full" onClick={() => void removeOldAndSignOut()}>
            {t.replaceRemoveOldFirst}
          </Button>
        </div>
      ) : (
        <TotpEnrollmentStep
          reauthPreferKind={voluntary ? 'totp' : 'sms'}
          onEnrolled={() => void removeOldAndSignOut()}
          onError={(err) => {
            if (err instanceof TooManySecondFactorsError && oldTotpIds.length > 0) {
              setPhase('refused');
            }
          }}
        />
      )}

      <div className="mt-4 text-center text-xs">
        {voluntary ? (
          <Link to={SECURITY_SETTINGS_PATH} className="text-muted-foreground hover:underline">
            {t.cancel}
          </Link>
        ) : (
          <button
            type="button"
            className="text-muted-foreground hover:underline"
            onClick={() => void signOut()}
          >
            {t.enrollSignOut}
          </button>
        )}
      </div>
    </AuthScreenLayout>
  );
}
